import { useMemo } from 'react'

import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'
import type { CartItemId } from 'models/purchaseFlowRevamp'

import { usePfStore } from '../../store'
import { resolveTabsFromCartAndCatalogue } from '../../steps/configuration/resolveTabs'
import { floorPlanProductFacts, type FloorPlanProductFacts } from './useFloorPlanProductFacts'

/** A cart line that carries a floor-plan configuration, with its catalogue facts. */
export interface FloorPlanLine extends FloorPlanProductFacts {
  id: CartItemId
}

/** The cart lines that carry a floor-plan configuration, in cart order — the lines that share one. */
export function useFloorPlanLines(): FloorPlanLine[] {
  const items = usePfStore((s) => s.items)
  const allowFallback = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)

  const catalogue = usePfCatalogue().data

  return useMemo(() => {
    const shells = catalogue?.shellProducts ?? []
    return resolveTabsFromCartAndCatalogue(items, shells, allowFallback)
      .filter((tab) => tab.flowId === 'floor_plan_config')
      .map((tab) => {
        const item = items.find((i) => i.id === tab.cartItemId)
        const shell = shells.find((p) => p.id === item?.shellProductId) ?? null
        const variant = shell?.variants?.find((v) => v.id === item?.variantId) ?? null
        return { id: tab.cartItemId, ...floorPlanProductFacts(shell, variant) }
      })
  }, [items, catalogue, allowFallback])
}

/** The ids of {@link useFloorPlanLines}. */
export function useFloorPlanLineIds(): CartItemId[] {
  const lines = useFloorPlanLines()
  return useMemo(() => lines.map((line) => line.id), [lines])
}
